import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import TypewriterText, { TextChunk } from 'routes/smart-quote/components/prompt-results/components/text-writer';
import { useGroupProductBySolveMode } from 'routes/smart-quote/hooks/use-group-prompt-results';
import { SmartQuotePrompt } from 'state/workspaces/smart-quote/model';

type ResultsSummaryProps = {
  prompt: SmartQuotePrompt;
};

const ResultsSummary = ({ prompt: { selectedOnBehalfClient, totalCount = 0, results } }: ResultsSummaryProps) => {
  const { formatMessage } = useIntl();
  const groupedProductsBySolveMode = useGroupProductBySolveMode(results);

  const client = selectedOnBehalfClient?.label;

  const summaryTextChunks = useMemo(() => {
    const initLabel = formatMessage({ id: 'smartQuote.workspace.main.resultsSummary.initLabel' });

    const clientLabel = client
      ? formatMessage({ id: 'smartQuote.workspace.main.resultsSummary.clientLabel.withClient' }, { client })
      : formatMessage({ id: 'smartQuote.workspace.main.resultsSummary.clientLabel.noClient' });

    const totalCountLabel = formatMessage(
      { id: 'smartQuote.workspace.main.resultsSummary.totalCountLabel' },
      { totalCount },
    );

    const chunks: TextChunk[] = [{ value: initLabel }, { value: clientLabel }, { value: totalCountLabel }];

    groupedProductsBySolveMode.forEach(([solveMode, products], index) => {
      const solveModeLabel = formatMessage(
        { id: 'smartQuote.workspace.main.resultsSummary.solveModeLabel' },
        { solveMode },
      );

      const productsLabel = formatMessage(
        { id: 'smartQuote.workspace.main.resultsSummary.productsLabel' },
        { products: Array.from(products).join(', ') },
      );

      const trailingLabel =
        index === groupedProductsBySolveMode.length - 1
          ? '.'
          : formatMessage({ id: 'smartQuote.workspace.main.resultsSummary.trailingLabel' });

      chunks.push({ value: solveModeLabel });
      chunks.push({ value: productsLabel, bold: true, underline: true });
      chunks.push({ value: trailingLabel });
    });

    return chunks;
  }, [client, groupedProductsBySolveMode, formatMessage, totalCount]);

  return (
    <p className="fs-16 mb-3">
      <TypewriterText chunks={summaryTextChunks} />
      <br />
    </p>
  );
};

export default ResultsSummary;
